import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { appModernizationDetails } from '../../../../../config';
import { CommonService } from './common.service';
import { BULK_MAINTENANCE_ERROR_MESSAGES } from './bulk-maintenance-error-messages';
import { SharedAuthService } from '../../../../../shared/shared.auth.service';


@Injectable({
  providedIn: 'root'
})
export class AppPaduService {
  private paduRequiredHeaders = [
    'Update_Type(U)',
    'GLOBAL APP ID',
    'LOB ID',
    'Sub LOB ID',
    'Application PADU',
    'Application PADU T1',
    'Application PADU T2',
    'Application PADU T3',
    'Application PADU T4',
    'Application PADU T5',
    'Application PADU T6',
    'Decom Forecast QTR',
    'Decom Forecast Year',
    'Notes / Comments',
    'RoadMap',
    'Replacement App',
    'Initiative / Program',
    'Business Owned',
    'Tech/Business Alignment Needed',
    'Non Integrated Tech',
    'Migration Forecast Year',
    'Migration Forecast QTR',
    'Application Strategy',
  ];
  private PADUStatus = [
    'PADU Pending',
    'Preferred',
    'Acceptable',
    'Discouraged',
    'Unacceptable',
    'Retired',
    'To Be Retired',
  ];
  private apiUrl: string;
  private config: any;

  private userDtls: any;
  private largeApiUrl: string = environment.LargeapiUrl;
  constructor(private http: HttpClient, private commonService: CommonService,
    private sharedAuthService: SharedAuthService) {
    this.config = appModernizationDetails;
    this.apiUrl = environment.apiUrl;
    const currentUser = sessionStorage.getItem('currentUser') || '';
    this.userDtls = {
      cn: this.sharedAuthService.getMsid(),
      mail: sessionStorage.getItem('loggedInUserEmail') || '',
      givenName: currentUser ? currentUser.trim().split(',')[1].trim() : '',
      sn: currentUser ? currentUser.trim().split(',')[0].trim() : '',
    }
  }

  public checkRequiredHeaders(headers: string[]) {
    const missingHeader = this.paduRequiredHeaders.filter(item => !headers.includes(item))
    return missingHeader;
  }
  public modifyHeaders(headers: string[]) {
    const newHeaders = [];
    for (const item of headers) {
      let isMatch = false;
      for (const data of this.paduRequiredHeaders) {
        if (item?.trim().toLowerCase() == data.toLowerCase()) {
          isMatch = true;
          newHeaders.push(data);
          break;
        }
      }
      if (!isMatch) {
        newHeaders.push(item);
      }
    }
    return newHeaders;
  }

  public modifyUploadedData(data: any[], headers: string[]) {
    const updatedData = [];
    for (const item of data) {
      const newData = this.transformDataItem(item, headers);
      updatedData.push(newData);
    }
    return updatedData;
  }

  private transformDataItem(item: any, headers: string[]): any {
    const newData = {};
    for (const key of Object.keys(item)) {
      const header = headers.find(ele => ele.toLowerCase() == key?.trim().toLowerCase());
      const val = item[key];
      newData[header] = this.transformValue(header, val, item[key]);
    }
    return newData;
  }

  private transformValue(header: string, val: any, originalValue: any): any {
    if (val == null) {
      return val;
    }

    if (header == 'GLOBAL APP ID') {
      return val.toUpperCase();
    }

    if (header.toLowerCase().includes('application padu')) {
      return this.matchPADUStatus(val);
    }

    if (this.isCapitalizableHeader(header)) {
      return this.capitalizeValue(val);
    }

    return originalValue;
  }

  private matchPADUStatus(val: any): any {
    const check = this.PADUStatus.filter((item) => item.toLowerCase() == val.toLowerCase());
    return check.length > 0 ? check[0] : val;
  }

  private isCapitalizableHeader(header: string): boolean {
    const capitalizableHeaders = [
      'LOB ID',
      'Sub LOB ID',
      'Tech/Business Alignment Needed',
      'Initiative / Program',
      'Business Owned',
      'RoadMap',
      'Application Strategy'
    ];
    return capitalizableHeaders.includes(header);
  }

  private capitalizeValue(val: any): any {
    const arrString = val.split(" ");
    if (arrString.length > 1) {
      return arrString.map((item) => item.charAt(0).toUpperCase() + item.slice(1)).join(" ");
    }

    return val ? `${val[0].toUpperCase()}${val.slice(1)}` : val;
  }

  public submitAppPaduData(body: any, type: string): Observable<any> {
    let url = '';

    body['userDetails'] = this.userDtls;
    if (type === 'Padu_Save') {
      url = this.largeApiUrl + this.config.routerpath.paduRoadmapBulkSave;
    } else {
      url = this.largeApiUrl + this.config.routerpath.paduRoadmapBulkImport;
    }
    const headers = new HttpHeaders({
      'Content-Type': ''
    });
    const options = {
      headers: headers
    }
    return this.http.put<any>(url, body, options);
  }

  public validateAppPaduData(excelData: any[]) {
    try {
      const clonedExcelData = excelData.map(el => ({ ...el }));
      const uniqueKeyNames = this.commonService.appPaduAllFields();
      const { uniques, duplicates } = this.commonService.findDuplicatesAndUniqueRecords(clonedExcelData, uniqueKeyNames); // { uniqueRecords, duplicates }
      //rename Message Key to 'Error Message' in duplicates for consistency
      duplicates.forEach(item => {
        if (item['Message']) {
          Object.assign(item, { 'Error Message': item['Message'] });
          delete item['Message'];
        }
      });
      let duplicateRecords = duplicates;
      let successRecords = [];
      let errorRec = [];
      uniques.forEach((ele, index) => {
        this.commonService.trimObjValues(ele);
        const validatedRecord = this.validateUniqueRecords(ele, index);
        if (validatedRecord['Status'] === 'Success') {
          successRecords.push(validatedRecord);
        }
        else if (validatedRecord['Status'] === 'Fail') {
          errorRec.push(validatedRecord);
        }
      });
      return { successRecords, duplicateRecords, errorRec };
    } catch (error) {
      console.error('Validation failed:', error);
    }
  }

  validateUniqueRecords(ele: any, index: number) {
    let requiredFields = [
      { key: 'Update_Type(U)', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.UPDATE_TYPE_EMPTY, nullCheck: true },
      { key: 'Update_Type(U)', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.UPDATE_TYPE_INVALID, validCheck: true },
      { key: 'GLOBAL APP ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.GLOBAL_APP_ID_MISSING, nullCheck: true },
      { key: 'LOB ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.LOB_ID_MISSING, nullCheck: true },
      { key: 'Sub LOB ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.SUB_LOB_ID_MISSING, nullCheck: true },
      { key: 'Application PADU', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU'), nullCheck: true },
      { key: 'Application PADU T1', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T1'), nullCheck: true },
      { key: 'Application PADU T2', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T2'), nullCheck: true },
      { key: 'Application PADU T3', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T3'), nullCheck: true },
      { key: 'Application PADU T4', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T4'), nullCheck: true },
      { key: 'Application PADU T5', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T5'), nullCheck: true },
      { key: 'Application PADU T6', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application PADU T6'), nullCheck: true },
      // { key: 'Decom Forecast QTR', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Decom Forecast QTR'), nullCheck: true },
      // { key: 'Decom Forecast Year', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Decom Forecast Year'), nullCheck: true },
      // { key: 'Application Strategy', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Application Strategy'), nullCheck: true },
      // { key: 'RoadMap', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('RoadMap'), nullCheck: true },
      // { key: 'Tech/Business Alignment Needed', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Tech/Business Alignment Needed'), nullCheck: true },
      // { key: 'Business Owned', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Business Owned'), nullCheck: true },
      // { key: 'Initiative / Program', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Initiative / Program'), nullCheck: true },
      // { key: 'Non Integrated Tech', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Non Integrated Tech'), nullCheck: true },
      // { key: 'Migration Forecast QTR', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Migration Forecast QTR'), nullCheck: true },
      // { key: 'Migration Forecast Year', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Migration Forecast Year'), nullCheck: true },
      // { key: 'Notes / Comments', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Notes / Comments'), nullCheck: true },
      // { key: 'Replacement App', message: BULK_MAINTENANCE_ERROR_MESSAGES.BUSINESS_SUPPORT.INVALID_REQUIRED_VALUE('Replacement App'), nullCheck: true }
    ];



    let obj = this.commonService.initializeValidationObject();
    obj = this.commonService.validateFieldsAndUpdateType(requiredFields, ele, index, obj, ['U']);
    if (obj.status === false) {
      ele['Status'] = 'Fail';
      ele['Error Message'] = obj.message;
    } else {
      ele['Status'] = 'Success';
      ele['Error Message'] = '';
    }
    return ele;
  }
}